import { api } from '@/helpers/api'
import useNotification from '@/composables/useNotification.js'
import useKeywords from '@/composables/useKeywords.js'

import { ref, computed, watchEffect, watch } from 'vue'
import { useAuth } from './useAuth'
import { useGoogleAPI } from '@/composables/utils/useGoogleAPI'
import { useMediaUploader } from '@/composables/ui/useMediaUploader'
import { useAudioUploader } from '@/composables/ui/useAudioUploader'
import { usePopup } from '@/composables/ui/usePopup'
import { useValidation } from '@/composables/utils/useValidation'

const USstates = [
    ['Alabama', 'AL'],
    ['Alaska', 'AK'],
    ['Arizona', 'AZ'],
    ['Arkansas', 'AR'],
    ['California', 'CA'],
    ['Colorado', 'CO'],
    ['Connecticut', 'CT'],
    ['Delaware', 'DE'],
    ['Florida', 'FL'],
    ['Georgia', 'GA'],
    ['Hawaii', 'HI'],
    ['Idaho', 'ID'],
    ['Illinois', 'IL'],
    ['Indiana', 'IN'],
    ['Iowa', 'IA'],
    ['Kansas', 'KS'],
    ['Kentucky', 'KY'],
    ['Louisiana', 'LA'],
    ['Maine', 'ME'],
    ['Maryland', 'MD'],
    ['Massachusetts', 'MA'],
    ['Michigan', 'MI'],
    ['Minnesota', 'MN'],
    ['Mississippi', 'MS'],
    ['Missouri', 'MO'],
    ['Montana', 'MT'],
    ['Nebraska', 'NE'],
    ['Nevada', 'NV'],
    ['New Hampshire', 'NH'],
    ['New Jersey', 'NJ'],
    ['New Mexico', 'NM'],
    ['New York', 'NY'],
    ['North Carolina', 'NC'],
    ['North Dakota', 'ND'],
    ['Ohio', 'OH'],
    ['Oklahoma', 'OK'],
    ['Oregon', 'OR'],
    ['Pennsylvania', 'PA'],
    ['Rhode Island', 'RI'],
    ['South Carolina', 'SC'],
    ['South Dakota', 'SD'],
    ['Tennessee', 'TN'],
    ['Texas', 'TX'],
    ['Utah', 'UT'],
    ['Vermont', 'VT'],
    ['Virginia', 'VA'],
    ['Washington', 'WA'],
    ['West Virginia', 'WV'],
    ['Wisconsin', 'WI'],
    ['Wyoming', 'WY']
]

const URLS = {
    icons: {
        vectorUp: '/images/icons/vector-up.svg',
        vectorDown: '/images/icons/vector-down.svg'
    },
    xhr: {
        mutate: {
            info: '/api/profile/v1/info/artist'
        }
    }
}
const firstFormValues = ref({
    band_name: '',
    home_location: '',
    city: '',
    state: '',
    zip: '',
    lat: 0,
    lng: 0
})
const secondFormValues = ref({
    medias: [],
    audios: [],
    keyword: ''
})
const missingStateValue = ref(false)

export const useArtistRegister = () => {
    const { isValid } = useValidation()
    const { persistKeywords, keywordInput } = useKeywords()
    const { notification } = useNotification()
    const { audios, updateArtistAudio } = useAudioUploader()
    const { apiLoaded, initialize, lat, lng, fullAddress } = useGoogleAPI()
    const { medias, updateProfileMedia, updateProfileAvatar } = useMediaUploader()
    const { popup: dropdown, popupOpen: dropdownOpen, togglePopup: toggleSelectDropdown } = usePopup()
    const { ARTIST_INFO_DATA, load, userValues, fetchUserInfo, updateProfileOrEventSteps } = useAuth()

    const allArtistRequiredFieldsFilled = computed(
        () =>
            firstFormValues.value.band_name &&
            firstFormValues.value.home_location &&
            firstFormValues.value.city &&
            firstFormValues.value.state &&
            firstFormValues.value.zip &&
            firstFormValues.value.lat &&
            firstFormValues.value.lng
    )

    ;(() => _populateFirstFormValues())()

    watch(
        [() => firstFormValues.value.city, () => firstFormValues.value.state, () => firstFormValues.value.zip],
        ([city, state, zip]) => {
            if (!city && !zip && !state) {
                missingStateValue.value = false
            } else if (!state) {
                missingStateValue.value = true
            } else if (state) {
                missingStateValue.value = false
            }
        }
    )
    watchEffect(() => {
        if (apiLoaded.value) {
            initialize({
                mode: 'geocoder',
                location: {
                    city: firstFormValues.value.city,
                    state: firstFormValues.value.state,
                    zip: firstFormValues.value.zip
                }
            })
        }
    })
    watchEffect(() => {
        if (lat.value && lng.value && fullAddress.value) {
            firstFormValues.value.lat = lat.value
            firstFormValues.value.lng = lng.value
            firstFormValues.value.home_location = fullAddress.value
        }
    })

    function handleSelectState(state) {
        firstFormValues.value.state = state
    }
    function handleFirstFormContinue(v) {
        if (!firstFormValues.value.state) {
            missingStateValue.value = true
        }
        if (isValid(v) && !missingStateValue.value) {
            _populateUserValues()
            updateProfileOrEventSteps('artist-register-two', 'artist-register-two')
        } else {
            notification('error', 'Incorrect required form fields.')
        }
    }
    function handleFirstFormBack() {
        _populateUserValues()
        updateProfileOrEventSteps('profile-register', 'artist-register-one')
    }
    function handleSecondFormBack() {
        _populateUserValues()
        updateProfileOrEventSteps('artist-register-one', 'artist-register-one')
    }
    function handleToggleDropdown(e) {
        toggleSelectDropdown(e)
    }
    async function handleRegisterArtist(nextStep = true) {
        _populateUserValues()

        const info = _prepArtistInfo()

        await _updateArtistMedia()
        await _updateArtistInfo(info)
        await fetchUserInfo()

        if (nextStep) {
            updateProfileOrEventSteps('artist-finalize-reg', 'artist-finalize-reg')
        }
    }
    async function _updateArtistInfo(info) {
        try {
            load.value = true
            const data = await api({
                method: 'post',
                url: `${URLS.xhr.mutate.info}/${userValues.value.id}`,
                data: {
                    _method: 'PUT',
                    ...info
                }
            })
            return data
        } finally {
            load.value = false
        }
    }
    async function _updateArtistMedia() {
        try {
            load.value = true
            await updateProfileAvatar('artist')
            await updateProfileMedia(medias.value, 'artist', { avatarUpload: false })
            await updateArtistAudio(audios.value)
        } finally {
            load.value = false
        }
    }
    function _populateUserValues() {
        userValues.value.bandName = firstFormValues.value.band_name
        userValues.value.homeLocation = firstFormValues.value.home_location
        userValues.value.lat = firstFormValues.value.lat
        userValues.value.lng = firstFormValues.value.lng
        userValues.value.city = firstFormValues.value.city
        userValues.value.state = firstFormValues.value.state
        userValues.value.zip = firstFormValues.value.zip
        userValues.value.medias = medias.value
        userValues.value.audios = audios.value
        if (persistKeywords.value.length) {
            userValues.value.keywords = persistKeywords.value.join(',')
        }
    }
    function _populateFirstFormValues() {
        firstFormValues.value.band_name = userValues.value.bandName
        firstFormValues.value.home_location = userValues.value.homeLocation
        firstFormValues.value.lat = userValues.value.lat
        firstFormValues.value.lng = userValues.value.lng
        firstFormValues.value.city = userValues.value.city
        firstFormValues.value.state = userValues.value.state
        firstFormValues.value.zip = userValues.value.zip
    }
    function _prepArtistInfo() {
        let info = { band_email: userValues.value.email }
        for (const [key, val] of Object.entries(ARTIST_INFO_DATA)) {
            info = {
                ...info,
                [val]: userValues.value[key]
            }
        }

        const isCurrentTagValid = /^[ &a-zA-Z0-9-_,;]+$/.test(keywordInput.value)
        if (isCurrentTagValid) {
            const keywords = [...info.keyword.split(','), keywordInput.value.split(/[,;]/)].filter((v) => v).join(',')

            info.keyword = keywords
        }

        return info
    }
    return {
        URLS,
        USstates,
        dropdown,
        dropdownOpen,
        userValues,
        missingStateValue,
        firstFormValues,
        secondFormValues,
        allArtistRequiredFieldsFilled,

        handleToggleDropdown,
        handleSelectState,
        handleFirstFormContinue,
        handleFirstFormBack,
        handleRegisterArtist,
        handleSecondFormBack
    }
}
